import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorMode,
  SimpleGrid,
  Icon,
  Flex,
  List,
  ListItem,
  ListIcon,
  Divider,
  Badge,
  Button,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { NavbarWithCallToAction } from '../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../../brandConfig';
import { FaShieldAlt, FaGlobe, FaLock, FaCheckCircle, FaCoins, FaNetworkWired, FaRocket, FaArrowRight } from 'react-icons/fa';
import { GiCardRandom, GiPokerHand } from 'react-icons/gi';
import ListBuildingModal from '../../components/ListBuildingModal';

export const WhatIsBlock52 = () => {
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const features = [
    {
      icon: GiCardRandom,
      title: 'Universal Card Functions',
      description: 'Standardized cryptographic functions for all 52-card deck mechanics, from shuffling to dealing to revealing.',
      color: '#3b82f6'
    },
    {
      icon: FaShieldAlt,
      title: 'Provably Fair',
      description: 'Every shuffle, deal, and outcome is cryptographically verifiable. No trust required.',
      color: '#64ffda'
    },
    {
      icon: FaNetworkWired,
      title: 'Decentralized Network',
      description: 'Distributed validator nodes ensure no single point of failure or control.',
      color: '#3b82f6'
    },
    {
      icon: FaLock,
      title: 'Secure by Design',
      description: 'Built on battle-tested Cosmos SDK with advanced cryptography for card game integrity.',
      color: '#64ffda'
    },
    {
      icon: FaRocket,
      title: 'High Performance',
      description: 'Fast block times and optimized for real-time gameplay with minimal latency.',
      color: '#3b82f6'
    },
    {
      icon: FaGlobe,
      title: 'Open Ecosystem',
      description: 'Anyone can build poker clubs, tournaments, and card game applications on top.',
      color: '#64ffda'
    }
  ];

  const useCases = [
    'Online poker rooms with provable fairness',
    'Tournament platforms with transparent prize pools',
    'Private poker clubs with automated rake distribution',
    'Card game development platforms',
    'Gambling compliance and audit systems',
    'Cross-border gaming with instant settlements'
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="900px">
          <VStack spacing={8} align="start">
            {/* Header */}
            <VStack spacing={4} align="start" w="full">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                LEARN
              </Badge>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                What is Block52?
              </Heading>
              <Text fontSize="xl" color={getColor('text.secondary', colorMode)} lineHeight="tall">
                Block52 is a purpose-built blockchain for the 52-card deck system. It provides universal,
                cryptographically secure functions for all card game mechanics—from shuffling and dealing
                to revealing and verifying outcomes.
              </Text>
            </VStack>

            <Divider />

            {/* The Vision Section */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                The Vision
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Traditional online card games require players to trust the platform operator. Block52 eliminates
                this trust requirement by moving all card game logic onto a transparent, decentralized blockchain.
              </Text>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Every action in a card game—shuffling, dealing, betting, revealing—is handled by cryptographic
                functions that are mathematically provable and publicly verifiable. The blockchain creates an
                immutable record of every game, ensuring fairness and transparency for all participants.
              </Text>
              <Box
                p={6}
                bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="#3b82f6"
              >
                <Text color={getColor('text.secondary', colorMode)} fontWeight="medium" fontStyle="italic">
                  "The vision is to create universal functions that fulfill all card game functions and
                  mechanisms through fair use of cryptography online—making card games trustless, transparent,
                  and truly decentralized."
                </Text>
              </Box>
            </Box>

            {/* Key Features Grid */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={6} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Key Features
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {features.map((feature, index) => (
                  <Box
                    key={index}
                    p={6}
                    borderRadius="lg"
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-4px)',
                      borderColor: feature.color,
                      boxShadow: `0 8px 16px ${feature.color}30`,
                    }}
                  >
                    <Flex align="start" mb={3}>
                      <Flex
                        w="48px"
                        h="48px"
                        borderRadius="lg"
                        bg={`${feature.color}20`}
                        align="center"
                        justify="center"
                        mr={4}
                        flexShrink={0}
                      >
                        <Icon as={feature.icon} color={feature.color} boxSize={6} />
                      </Flex>
                      <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        {feature.title}
                      </Heading>
                    </Flex>
                    <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                      {feature.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* How It Works Section */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                How It Works
              </Heading>
              <VStack spacing={4} align="start">
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(59, 130, 246, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#3b82f6">1</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Cryptographic Shuffling
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        True randomness is generated using verifiable random functions (VRFs) that cannot be
                        predicted or manipulated by any party.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(100, 255, 218, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#64ffda">2</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Commit-Reveal Schemes
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Cards are dealt using cryptographic commitments that prevent cheating while maintaining
                        privacy until the reveal phase.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(59, 130, 246, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#3b82f6">3</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        On-Chain Verification
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Every game action is recorded on the blockchain, creating an immutable audit trail
                        that anyone can verify.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(100, 255, 218, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#64ffda">4</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Instant Settlements
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Winnings are automatically distributed via smart contract logic—no withdrawal delays,
                        no payment processor fees.
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </VStack>
            </Box>

            {/* Use Cases */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                What Can You Build?
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} mb={4}>
                Block52 provides the foundational infrastructure for a new generation of card game applications:
              </Text>
              <List spacing={3}>
                {useCases.map((useCase, index) => (
                  <ListItem key={index}>
                    <Flex align="center">
                      <ListIcon as={FaCheckCircle} color="#64ffda" fontSize="xl" />
                      <Text color={getColor('text.secondary', colorMode)}>{useCase}</Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Technical Foundation */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Technical Foundation
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Block52 is built on the Cosmos SDK, leveraging battle-tested blockchain technology with
                custom modules designed specifically for card game mechanics. The network uses a Proof of
                Stake consensus mechanism secured by validator nodes staking B52 tokens.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box
                  p={4}
                  bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <Icon as={FaNetworkWired} color="#3b82f6" boxSize={8} mb={2} />
                  <Text fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Cosmos SDK
                  </Text>
                  <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>
                    Enterprise-grade blockchain framework
                  </Text>
                </Box>
                <Box
                  p={4}
                  bg={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.1)' : 'teal.50'}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <Icon as={FaCoins} color="#64ffda" boxSize={8} mb={2} />
                  <Text fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Proof of Stake
                  </Text>
                  <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>
                    Energy-efficient consensus
                  </Text>
                </Box>
                <Box
                  p={4}
                  bg={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'blue.50'}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <Icon as={GiPokerHand} color="#3b82f6" boxSize={8} mb={2} />
                  <Text fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Custom Modules
                  </Text>
                  <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>
                    Purpose-built for card games
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            {/* Why Block52? */}
            <Box
              w="full"
              p={6}
              bgGradient={colorMode === 'dark'
                ? 'linear(to-r, rgba(59, 130, 246, 0.1), rgba(100, 255, 218, 0.1))'
                : 'linear(to-r, blue.50, teal.50)'}
              borderRadius="xl"
              border="2px solid"
              borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#3b82f6'}
            >
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Why Block52?
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={3}>
                Existing online card game platforms require players to trust centralized operators. Players
                can't verify if shuffles are fair, if outcomes are manipulated, or if rake calculations are
                accurate.
              </Text>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                Block52 changes this by putting all game logic on a transparent, decentralized blockchain.
                Every shuffle, every deal, every outcome is cryptographically verifiable. No trust required—just
                math and open source code.
              </Text>
            </Box>

            {/* CTA */}
            <Box w="full" textAlign="center" py={6}>
              <Button
                size="lg"
                px={8}
                py={6}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: 'linear(to-r, #64ffda, #3b82f6)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)',
                }}
                rightIcon={<FaArrowRight />}
                borderRadius="full"
                fontWeight="bold"
                onClick={() => setIsModalOpen(true)}
              >
                Start Now
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
      <ListBuildingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default WhatIsBlock52;
